<template>
  <RCard class="prices relative-position">
    <ConfirmationDialog
      ref="confirmDialog"
      confirm-label="Yes, delete"
      confirm-btn-type="danger"
      cancel-label="no, keep subscription period"
    />

    <FormSubscriptionPeriod
      v-if="showSubscriptionModal && latestSubscriptionPeriod"
      :original-tasks="originalTasks"
      :subscription-period="latestSubscriptionPeriod"
      :subscription-id="subId"
      :showing="showSubscriptionModal"
      :disable-properties="['product_id']"
      @refresh="refreshData('created')"
      @close="closeFormModal()"
    />

    <ItemsModal ref="itemsModal" />

    <AgGrid
      v-if="vendors"
      ref="agGrid"
      :options="gridOptions"
      :row-data="clonedPeriods"
      @ready="onGridReady"
      @cell-value-changed="um_update"
    >
      <template #header>
        <Grid gaf="column" justify-content="space-between" align-items="center" class="q-pb-md">
          <div class="text-subtitle-1 text-secondary-2">Prices</div>

          <Grid gaf="column" align-items="center" gap="var(--r-space-md)">
            <RBtn type="text" size="small" @click="showFormModal">Create new subscription period</RBtn>
            <ShowHideCols :ag-grid="agGrid" />
          </Grid>
        </Grid>
      </template>
    </AgGrid>

    <QInnerLoading :showing="!vendors">
      <QSpinnerHourglass size="3.5rem" color="primary-1" />
    </QInnerLoading>
  </RCard>
</template>

<script>
import * as apiTo from '@/modular-api'
import gridOptions from './gridOptions'
import updateMethods from './updateMethods'
// Libraries
import { cloneDeep } from 'lodash'
import { mapGetters } from 'vuex'
// Components
import AgGrid from '@/ag-grid/AgGrid'
import ShowHideCols from '@/ag-grid/components/show-hide-cols/ShowHideCols'
import FormSubscriptionPeriod from '@/components/forms/form-subscription-period/FormSubscriptionPeriod'
import ConfirmationDialog from '@/ui/ConfirmationDialog'
import ItemsModal from './items-modal/ItemsModal.vue'

export default {
  components: {
    AgGrid,
    ShowHideCols,
    FormSubscriptionPeriod,
    ConfirmationDialog,
    ItemsModal
  },

  mixins: [gridOptions, updateMethods],

  props: {
    subId: [Number, String]
  },

  data: () => ({
    showSubscriptionModal: false,
    vendors: null,
    publishers: null,
    originalTasks: null,
    clonedPeriods: null,
    financialPeriods: [],
    agGrid: null,
    tableStateColumns: null
  }),

  computed: {
    ...mapGetters('subscription-show', {
      periods: 'subscriptionPeriods',
      subscription: 'subscription'
    }),

    latestSubscriptionPeriod() {
      let periods = cloneDeep(this.periods)
      periods.sort((a, b) => new Date(b.start_date) - new Date(a.start_date))
      let latestPeriod = periods[0]
      latestPeriod.product = this.subscription.product
      return latestPeriod
    },

    financialPeriodOptions() {
      return this.financialPeriods.map(period => ({
        label: `${this.formatDateForLocale(period.start_date)} - ${this.formatDateForLocale(period.end_date)}`,
        value: period
      }))
    }
  },

  watch: {
    periods: {
      handler(val) {
        if (!val.length) return
        const notClonedPeriods = !this.clonedPeriods
        const periodsLengthDiffers = () => this.clonedPeriods.length !== val.length
        const updatedPeriods = cloneDeep(val)
        if (notClonedPeriods || periodsLengthDiffers()) this.clonedPeriods = updatedPeriods
        else this.$refs.agGrid.updateRows(updatedPeriods)
      },
      immediate: true,
      deep: true
    }
  },

  created() {
    apiTo.GET_FINANCIAL_PERIODS().then(({ data }) => (this.financialPeriods = data.result))
    this.getAndSetVendorsPublishersTableState()
  },

  beforeDestroy() {
    this.saveTableState()
  },

  methods: {
    onGridReady(agGrid) {
      this.agGrid = agGrid
      this.tableStateColumns && this.agGrid.columnApi.setColumnState(this.tableStateColumns)
    },

    notificationAfterAcceptOrCancel(response) {
      this.$store.dispatch(
        'createNotificationToast',
        {
          message: response.messages.default,
          color: response.status
        },
        { root: true }
      )
    },

    acceptPeriod(period) {
      apiTo.ACCEPT_SUBSCRIPTION_PERIOD(period.id).then(res => {
        this.$store.commit('subscription-show/updatePeriod', { ...period, status: { id: 1, name: 'accepted' } })
        this.notificationAfterAcceptOrCancel(res.data)
      })
    },

    cancelPeriod(period) {
      apiTo.CANCEL_SUBSCRIPTION_PERIOD(period.id).then(res => {
        this.$store.commit('subscription-show/updatePeriod', { ...period, status: { id: 3, name: 'cancelled' } })
        this.notificationAfterAcceptOrCancel(res.data)
      })
    },

    acceptOrCancelClicked(row, acceptOrCancel) {
      const period = this.periods.find(p => p.id === row.data.id)

      if (acceptOrCancel === 'accept') this.acceptPeriod(period)
      else this.cancelPeriod(period)
    },

    getAndSetVendorsPublishersTableState() {
      Promise.all([
        apiTo.GET_VENDORS(),
        apiTo.GET_PUBLISHERS(),
        apiTo.GET_LIST_VIEW_TABLE_STATE('subscriptions.prices')
      ]).then(([{ data: vendors }, { data: publishers }, { data: tableState }]) => {
        this.vendors = vendors.result
        this.publishers = publishers.result
        this.tableStateColumns = tableState.result.columns
      })
    },

    refreshData(result) {
      if (result === 'created') this.$emit('period-created')
      else if (result) {
        // updated
        this.$emit('period-updated', result)
        this.checkPriceInLocalCurrencyEdit()
      }

      let promises = [this.$store.dispatch('subscriptions/getSingle', { id: this.subId })]
      return Promise.all(promises).then(() => this.closeFormModal())
    },

    showFormModal() {
      apiTo
        .GET_TASK_APPLICABILITY_FOR_SUB(this.subscription.id)
        .then(({ data }) => (this.originalTasks = data.result.task_applicability))
      return this.$store
        .dispatch('subscriptions/subscriptionPeriods/getSingle', {
          id: this.latestSubscriptionPeriod.id
        })
        .then(() => {
          this.showSubscriptionModal = true
        })
    },

    checkPriceInLocalCurrencyEdit() {
      const agGrid = this.$refs.agGrid.getAgGrid()
      const editingCells = agGrid.gridApi.getEditingCells()
      if (!editingCells.length) return
      const {
        rowIndex,
        column: { colId }
      } = editingCells[0]
      if (['normalized_price', 'exchange_rate'].includes(colId)) {
        // we updated exchange rate and we are editing price in local currency
        agGrid.gridApi.stopEditing(true)
        agGrid.gridApi.startEditingCell({
          rowIndex,
          colKey: colId
        })
      }
    },

    closeFormModal() {
      this.showSubscriptionModal = false
    },

    onDelete(row) {
      apiTo.GET_SUBSCRIPTION_PERIOD_DELETE_CONFIRMATION(row.id).then(({ data }) => {
        const title = data.messages.default.join('\n')
        const onCancel = hide => hide()

        const dialog = this.$refs.confirmDialog.show({ title })

        dialog.onCancel(onCancel)
        dialog.onConfirm(this.deleteSubPeriod.bind(this, row.id))
      })
    },

    deleteSubPeriod(subPeriodId, hideModal, setModalLoading) {
      setModalLoading()
      apiTo
        .DELETE_SUBSCRIPTION_PERIOD(subPeriodId)
        .then(() => {
          hideModal()
          if (this.periods.length === 1) this.$router.push({ path: '/subscriptions' })
          else this.$emit('period-deleted', subPeriodId)
        })
        .finally(() => setModalLoading(false))
    },

    saveTableState() {
      const columns = this.agGrid.columnApi.getColumnState()
      apiTo.MODIFY_LIST_VIEW_TABLE_STATE('subscriptions.prices', { columns })
    },

    showItemsModal(row) {
      this.$refs.itemsModal.show(row)
    }
  }
}
</script>

<style lang="scss" scoped>
@import '~@/styles/mixins';

.prices {
  @include table-with-no-filters;

  min-height: 200px;
}
</style>
